import styled from '@emotion/styled';
import { Shield, Trophy } from 'lucide-react';
import { PlaceholderKey } from 'modules/manager/domain/placeholder/placeholder-keys';
import React from 'react';
import { theme } from 'styles/theme/theme';
import { Decor, Hot, Logo, Poster, PosterHeader, Subtitle, Title, Titles, useKeyText } from './scheme-parts';

/**
 * Several matches: MULTIPLE_SCHEDULE and MULTIPLE_RESULTS. The first row carries
 * the keys; the score of one team sits on the second row, where the two scores
 * are split — the way an author lays it out when they want them apart.
 */
const MultiMatchScheme: React.FC = () => {
  const text = useKeyText();
  const shield = (
    <Logo round>
      <Shield strokeWidth={1.5} />
    </Logo>
  );
  return (
    <Poster>
      <PosterHeader>
        <Hot k={PlaceholderKey.TOURNAMENT_LOGO}>
          <Logo>
            <Trophy strokeWidth={1.5} />
          </Logo>
        </Hot>
        <Titles>
          <Hot k={PlaceholderKey.CHAMPIONSHIP_NAME}>
            <Title>{text(PlaceholderKey.CHAMPIONSHIP_NAME)}</Title>
          </Hot>
          <Hot k={PlaceholderKey.TOURNAMENT_NAME}>
            <Subtitle>{text(PlaceholderKey.TOURNAMENT_NAME)}</Subtitle>
          </Hot>
        </Titles>
        <Round>
          <Hot k={PlaceholderKey.ROUND_NAME}>{text(PlaceholderKey.ROUND_NAME)}</Hot>
        </Round>
      </PosterHeader>
      <Row>
        <When>
          <Hot k={PlaceholderKey.MATCH_DATE}>{text(PlaceholderKey.MATCH_DATE)}</Hot>
          <Hot k={PlaceholderKey.MATCH_TIME}>{text(PlaceholderKey.MATCH_TIME)}</Hot>
        </When>
        <Side>
          <Hot k={PlaceholderKey.TEAM_LOGO}>{shield}</Hot>
          <Hot k={PlaceholderKey.TEAM_NAME}>
            <b>{text(PlaceholderKey.TEAM_NAME)}</b>
          </Hot>
          <Hot k={PlaceholderKey.TEAM_SHORT_NAME}>{text(PlaceholderKey.TEAM_SHORT_NAME)}</Hot>
        </Side>
        <Hot k={PlaceholderKey.SCORE} fallback={<Decor>—</Decor>}>
          <b>{text(PlaceholderKey.SCORE)}</b>
        </Hot>
        <Side>
          {shield}
          <Decor>{text(PlaceholderKey.TEAM_NAME, 1)}</Decor>
        </Side>
        <Hot k={PlaceholderKey.MATCH_VENUE}>{text(PlaceholderKey.MATCH_VENUE)}</Hot>
      </Row>
      <Row>
        <When>
          <Decor>{text(PlaceholderKey.MATCH_DATE, 1)}</Decor>
          <Decor>{text(PlaceholderKey.MATCH_TIME, 1)}</Decor>
        </When>
        <Side>
          {shield}
          <Decor>{text(PlaceholderKey.TEAM_NAME, 2)}</Decor>
        </Side>
        <Split>
          <Hot k={PlaceholderKey.SCORE_TEAM} fallback={<Decor>—</Decor>}>
            {text(PlaceholderKey.SCORE_TEAM)}
          </Hot>
          <Decor>:</Decor>
          <Decor>{text(PlaceholderKey.SCORE_TEAM, 1)}</Decor>
        </Split>
        <Side>
          {shield}
          <Decor>{text(PlaceholderKey.TEAM_NAME)}</Decor>
        </Side>
        <Decor>{text(PlaceholderKey.MATCH_VENUE, 1)}</Decor>
      </Row>
    </Poster>
  );
};

export default MultiMatchScheme;

const Round = styled.div`
  margin-left: auto;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid ${theme.colors.SurfaceIndependence40};
`;

const When = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;

const Side = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;

const Split = styled.div`
  display: flex;
  gap: 6px;
  font-weight: 700;
`;
